<template>
  <div class="security-messages">
    <h2 class="subheading grey--text">Comunicados y Consignas</h2>
    <v-container class="my-5">
      <v-layout row wrap>
        <v-flex xs12>
          <v-btn color="primary" dark @click="showCreateDialog = true">
            <v-icon left>add</v-icon>
            Enviar Comunicado
          </v-btn>
        </v-flex>
      </v-layout>

      <v-layout class="my-4" align-center justify-center fill-height>
        <v-flex>
          <v-card-title>
            Comunicados Enviados
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              append-icon="search"
              label="Buscar"
              single-line
              hide-details
              class="mr-3"
            ></v-text-field>
          </v-card-title>

          <v-alert v-if="securityError" type="error" dismissible class="mb-3">
            {{ securityError }}
          </v-alert>

          <v-data-table
            :headers="messageHeaders"
            :items="securityMessages"
            :search="search"
            :loading="isLoading"
            :rows-per-page-text="$t('filas_por_pagina')"
            :rows-per-page-items="[
              {text: '5', value: 5},
              {text: '10', value: 10},
              {text: '25', value: 25},
              {text: this.$t('todos'), value: -1}
            ]"
            class="elevation-1"
          >
            <template slot="items" slot-scope="props">
              <td class="text-xs-center">
                <v-icon
                  :color="getPriorityColor(props.item.priority)"
                  medium
                >
                  {{ getPriorityIcon(props.item.priority) }}
                </v-icon>
              </td>
              <td>{{ props.item.title }}</td>
              <td>{{ formatDate(props.item.created_at) }}</td>
              <td class="text-xs-center">
                <v-chip
                  small
                  :color="getPriorityColor(props.item.priority)"
                  text-color="white"
                >
                  {{ props.item.priority_display || props.item.priority }}
                </v-chip>
              </td>
              <td class="text-xs-center">
                <span class="green--text font-weight-bold">{{ props.item.read_count }}</span>
                /
                <span>{{ props.item.total_recipients }}</span>
              </td>
              <td class="text-xs-center">
                <v-progress-circular
                  :value="getReadPercentage(props.item)"
                  :color="getProgressColor(props.item)"
                  size="40"
                >
                  {{ getReadPercentage(props.item) }}%
                </v-progress-circular>
              </td>
              <td class="text-xs-center">
                <v-icon
                  medium
                  color="primary"
                  @click="viewReport(props.item)"
                  style="cursor: pointer;"
                >
                  visibility
                </v-icon>
              </td>
            </template>
            <template v-slot:no-results>
              <v-alert :value="true" color="error" icon="warning">
                {{$t('ningun_resultado_1')}} "{{ search }}" {{$t('ningun_resultado_2')}}
              </v-alert>
            </template>
          </v-data-table>
        </v-flex>
      </v-layout>
    </v-container>

    <!-- DIALOG: Crear Comunicado -->
    <v-dialog v-model="showCreateDialog" max-width="700px" persistent>
      <v-form ref="createForm" @submit.prevent="createMessage()">
        <v-card class="elevation-5">
          <v-toolbar dark color="primary">
            <v-toolbar-title>Enviar Comunicado</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon color="white" flat @click="closeCreateDialog" class="align-self-end">
              <v-icon>close</v-icon>
            </v-btn>
          </v-toolbar>

          <v-card-text class="pt-4">
            <v-layout row wrap>
              <v-flex xs12>
                <v-text-field
                  v-model="newMessage.title"
                  label="Título *"
                  required
                  @input="$refs.createForm.validate()"
                  placeholder="Ej: Actualización de Protocolo de Seguridad"
                ></v-text-field>
              </v-flex>

              <v-flex xs12>
                <v-textarea
                  v-model="newMessage.content"
                  label="Contenido *"
                  required
                  rows="6"
                  @input="$refs.createForm.validate()"
                  placeholder="Cuerpo del comunicado..."
                ></v-textarea>
              </v-flex>

              <v-flex xs12>
                <v-select
                  v-model="newMessage.priority"
                  :items="priorityOptions"
                  label="Prioridad"
                  item-value="value"
                  item-text="text"
                >
                  <template slot="item" slot-scope="data">
                    <v-layout row align-center>
                      <v-icon
                        :color="getPriorityColor(data.item.value)"
                        left
                      >
                        {{ getPriorityIcon(data.item.value) }}
                      </v-icon>
                      <span>{{ data.item.text }}</span>
                    </v-layout>
                  </template>
                  <template slot="selection" slot-scope="data">
                    <v-layout row align-center>
                      <v-icon
                        :color="getPriorityColor(data.item.value)"
                        left
                      >
                        {{ getPriorityIcon(data.item.value) }}
                      </v-icon>
                      <span>{{ data.item.text }}</span>
                    </v-layout>
                  </template>
                </v-select>
              </v-flex>

              <v-flex xs12>
                <v-alert type="info" :value="true" icon="info">
                  Este comunicado será enviado a todos los guardias activos de tu empresa.
                </v-alert>
              </v-flex>
            </v-layout>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn flat @click="closeCreateDialog">Cancelar</v-btn>
            <v-btn color="primary" type="submit" :loading="isLoading">Enviar</v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>

    <!-- DIALOG: Reporte de Lectura -->
    <v-dialog v-model="showReportDialog" max-width="800px" persistent>
      <v-card v-if="messageReport" class="elevation-5">
        <v-toolbar dark color="primary">
          <v-toolbar-title>Reporte de Lectura</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon color="white" flat @click="showReportDialog = false" class="align-self-end">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-4">
          <v-layout row wrap>
            <v-flex xs12>
              <h3>{{ messageReport.message.title }}</h3>
              <p class="grey--text">{{ formatDate(messageReport.message.created_at) }}</p>
            </v-flex>

            <!-- Progreso de lectura -->
            <v-flex xs12>
              <v-divider class="my-3"></v-divider>
              <p class="mb-2">
                <strong>{{ messageReport.report.read_count }} de {{ messageReport.report.total_recipients }} guardias han leído</strong>
              </p>
              <v-progress-linear
                :value="messageReport.report.read_percentage"
                :color="getProgressColor({ read_count: messageReport.report.read_count, total_recipients: messageReport.report.total_recipients })"
                height="25"
              >
                <strong>{{ messageReport.report.read_percentage }}%</strong>
              </v-progress-linear>
            </v-flex>

            <!-- Tabs: Leído / Sin leer -->
            <v-flex xs12>
              <v-divider class="my-3"></v-divider>
              <v-tabs v-model="reportTab" grow>
                <v-tab>
                  ✅ Leídos ({{ messageReport.details.read.length }})
                </v-tab>
                <v-tab>
                  ⏳ Sin Leer ({{ messageReport.details.unread.length }})
                </v-tab>

                <v-tab-item>
                  <v-list>
                    <v-list-tile
                      v-for="guard in messageReport.details.read"
                      :key="guard.guard_id"
                      avatar
                    >
                      <v-list-tile-avatar color="green">
                        <v-icon dark>check</v-icon>
                      </v-list-tile-avatar>
                      <v-list-tile-content>
                        <v-list-tile-title>{{ guard.guard_name }}</v-list-tile-title>
                        <v-list-tile-sub-title>Badge: {{ guard.badge_number }}</v-list-tile-sub-title>
                      </v-list-tile-content>
                      <v-list-tile-action>
                        <span class="grey--text caption">{{ formatDate(guard.read_at) }}</span>
                      </v-list-tile-action>
                    </v-list-tile>
                  </v-list>
                </v-tab-item>

                <v-tab-item>
                  <v-list>
                    <v-list-tile
                      v-for="guard in messageReport.details.unread"
                      :key="guard.guard_id"
                      avatar
                    >
                      <v-list-tile-avatar color="orange">
                        <v-icon dark>schedule</v-icon>
                      </v-list-tile-avatar>
                      <v-list-tile-content>
                        <v-list-tile-title>{{ guard.guard_name }}</v-list-tile-title>
                        <v-list-tile-sub-title>Badge: {{ guard.badge_number }}</v-list-tile-sub-title>
                      </v-list-tile-content>
                      <v-list-tile-action>
                        <span class="orange--text caption">Pendiente</span>
                      </v-list-tile-action>
                    </v-list-tile>
                  </v-list>
                </v-tab-item>
              </v-tabs>
            </v-flex>
          </v-layout>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="showReportDialog = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import {
  FETCH_SECURITY_MESSAGES,
  CREATE_SECURITY_MESSAGE,
  FETCH_MESSAGE_REPORT
} from '@/store/modules/security'
import {
  getPriorityColor,
  getPriorityIcon,
  formatMessageDate
} from '@/utils/securityMessageHelpers'

export default {
  name: 'security_messages',
  computed: {
    ...mapGetters('security', {
      isLoading: 'isSecurityLoading',
      securityError: 'getSecurityError',
      securityMessages: 'getSecurityMessages',
      messageReport: 'getMessageReport'
    })
  },
  data() {
    return {
      search: '',
      showCreateDialog: false,
      showReportDialog: false,
      reportTab: 0,
      newMessage: {
        title: '',
        content: '',
        priority: 'NORMAL'
      },
      priorityOptions: [
        { text: 'Urgente', value: 'URGENTE' },
        { text: 'Normal', value: 'NORMAL' },
        { text: 'Baja', value: 'BAJA' }
      ],
      messageHeaders: [
        { text: '', value: 'priority', sortable: false, align: 'center', width: '50px' },
        { text: 'Título', value: 'title' },
        { text: 'Fecha', value: 'created_at' },
        { text: 'Prioridad', value: 'priority', align: 'center' },
        { text: 'Leídos', value: 'read_count', align: 'center' },
        { text: 'Porcentaje', value: 'read_percentage', align: 'center', sortable: false },
        { text: 'Acciones', value: 'actions', sortable: false, align: 'center' }
      ]
    }
  },
  mounted() {
    this.fetchMessages()
  },
  methods: {
    ...mapActions('security', [
      FETCH_SECURITY_MESSAGES,
      CREATE_SECURITY_MESSAGE,
      FETCH_MESSAGE_REPORT
    ]),
    fetchMessages() {
      const token = this.$store.getters.getToken
      this[FETCH_SECURITY_MESSAGES](token)
        .catch(() => {
          // Error manejado en el store
        })
    },
    createMessage() {
      if (!this.newMessage.title || !this.newMessage.content) {
        this.$store.commit('snackbar_change_message', 'Por favor completa título y contenido')
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_activate_true')
        return
      }

      const token = this.$store.getters.getToken
      this[CREATE_SECURITY_MESSAGE]({
        token,
        title: this.newMessage.title,
        content: this.newMessage.content,
        priority: this.newMessage.priority
      })
        .then(resp => {
          this.$store.commit('snackbar_change_message', `Comunicado enviado a ${resp.total_recipients} guardias`)
          this.$store.commit('snackbar_change_color', 'success')
          this.$store.commit('snackbar_activate_true')
          this.closeCreateDialog()
          this.fetchMessages()
        })
        .catch(() => {
          this.$store.commit('snackbar_change_message', this.securityError || 'Error al crear comunicado')
          this.$store.commit('snackbar_change_color', 'error')
          this.$store.commit('snackbar_activate_true')
        })
    },
    viewReport(item) {
      const token = this.$store.getters.getToken
      this[FETCH_MESSAGE_REPORT]({
        token,
        messageId: item.id
      })
        .then(() => {
          this.showReportDialog = true
        })
        .catch(() => {
          this.$store.commit('snackbar_change_message', this.securityError || 'Error al cargar el reporte')
          this.$store.commit('snackbar_change_color', 'error')
          this.$store.commit('snackbar_activate_true')
        })
    },
    closeCreateDialog() {
      this.showCreateDialog = false
      this.$refs.createForm.reset()
      this.newMessage = {
        title: '',
        content: '',
        priority: 'NORMAL'
      }
    },
    formatDate: formatMessageDate,
    getPriorityColor,
    getPriorityIcon,
    getReadPercentage(item) {
      if (!item.total_recipients) return 0
      return Math.round((item.read_count / item.total_recipients) * 100)
    },
    getProgressColor(item) {
      const percentage = this.getReadPercentage(item)
      if (percentage === 100) return 'green'
      if (percentage >= 80) return 'blue'
      if (percentage >= 50) return 'orange'
      return 'red'
    }
  }
}
</script>

<style scoped>
.security-messages {
  padding: 20px;
}

.subheading {
  font-size: 24px;
  font-weight: 300;
}
</style>
